import FlatpickrController from 'controllers/flatpickr_controller'

const BROWSER_UTC_OFFSET = (function() {
  let date = new Date()
  return (date.getTimezoneOffset() * 60)
}())

export default class extends FlatpickrController {

  static get targets() {
    return [...super.targets, 'bookableBadge']
  }

  static classes = [
    'bookableBadge'
  ]

  static outlets = [
    'realtime-widget'
  ]

  /**
   * @returns {RealtimeWidgetController}
   */
  get widget() {
    return this.realtimeWidgetOutlet
  }

  /**
   * @returns {BookableController}
   */
  get bookable() {
    return this.widget && this.widget.booking
  }

  static values = {
    locale:                 { type: String, default: 'en'               },
    utcOffset:              { type: Number, default: BROWSER_UTC_OFFSET },
    availableFrom:          { type: String, default: null               },
    availableTill:          { type: String, default: null               },
    firstDay:               { type: String, default: null               },
    currentDateFormat:      { type: String, default: 'l, j F, Y'        },
    selectLabel:            { type: String, default: 'Choose a date'    },
    todayLabel:             { type: String, default: 'Today'            },
    currentWeekLabelFormat: { type: String, default: 'l'                },
    currentYearLabelFormat: { type: String, default: 'l, j F'           },
    otherYearLabelFormat:   { type: String, default: 'j F, Y'           }
  }

  // SETUP:

  connect () {
    this.flatpickrOptions = {
      static:      true,
      clickOpens:  false,
      appendTo:    this.element,
      altInput:    true,
      altFormat:   this.currentDateFormatValue
    }

    super.connect()

    this.flatpickr.altInput.setAttribute('data-action', 'click->realtime-widget--date-select#toggleDatePicker')

    this.lastValue = this.value

    this.refresh()
  }

  disconnect () {
    super.disconnect()
  }

  // EVENTS:

  refresh () {
    if (this.value) {
      if (this.isToday) {
        this.currentDateFormatValue = this.escapeDateFormat(this.todayLabelValue)
      } else if (this.isCurrentWeek) {
        this.currentDateFormatValue = this.currentWeekLabelFormatValue
      } else if (this.isCurrentYear) {
        this.currentDateFormatValue = this.currentYearLabelFormatValue
      } else {
        this.currentDateFormatValue = this.otherYearLabelFormatValue
      }
    } else {
      this.currentDateFormatValue = this.otherYearLabelFormatValue
    }

    if (this.flatpickr) {
      this.setDatePickerConfig('altFormat', this.currentDateFormatValue)
      this.flatpickr.redraw()
    }
  }

  jumpToDateWindow () {
    if (!this.flatpickr) { return }

    let fromWindow = this.hasAvailableFromValue && this.flatpickr.parseDate(this.availableFromValue)
    let tillWindow = this.hasAvailableTillValue && this.flatpickr.parseDate(this.availableTillValue)

    if (!fromWindow && !tillWindow) { return }

    let selectedDate = this.value || this.lastValue
    let selectedOoB  = selectedDate != null && (
                         (fromWindow && selectedDate < fromWindow) ||
                         (tillWindow && selectedDate > tillWindow)
                       )

    let visibleDate  = this.flatpickr.currentMonth != null &&
                       this.flatpickr.currentYear != null &&
                       this.flatpickr.parseDate(`${this.flatpickr.currentYear}-${String(this.flatpickr.currentMonth + 1).padStart(1, '0')}-01`)
    let visibleOoB   = visibleDate != null && (
                         (fromWindow && visibleDate < fromWindow) ||
                         (tillWindow && visibleDate > tillWindow)
                       )

    if (selectedOoB && visibleOoB) {
      // console.log('selected and visible date Out-of-Bounds, jumping to fromWindow', fromWindow)
      this.flatpickr.jumpToDate(fromWindow, true)
    } else if (!visibleOoB && visibleDate) {
      // console.log('visible date in bounds, jumping to it', visibleDate)
      this.flatpickr.jumpToDate(visibleDate, true)
    } else if (!selectedOoB && selectedDate) {
      // console.log('selected date in bounds, jumping to it', selectedDate)
      this.flatpickr.jumpToDate(selectedDate, true)
    } else {
      console.log('no date?', {
        visibleDate:  visibleDate,
        selectedDate: selectedDate,
        fromWindow:   fromWindow,
        tillWindow:   tillWindow
      })
    }
  }

  changed (event) {
    this.widget.refresh()

    if (this.value) {
      this.widget.reportEvent('date-changed', { date: this.value.toISOString().split('T').shift() })
    }
  }

  addBookableBadge () {
    if (!this.hasBookableBadgeTarget) {
      if (!this.flatpickr) { return }

      let calendarContainer = this.flatpickr.calendarContainer
      let bookableBadge = document.createElement('div')
      bookableBadge.classList.add(this.bookableBadgeClass)
      bookableBadge.setAttribute(`data-${this.identifier}-target`, 'bookableBadge')

      calendarContainer.appendChild(bookableBadge)
    }

    if (this.widget && this.widget.bookableSelect && this.widget.bookableSelect.bookables.length > 1) {
      let bookable = this.bookable
      this.bookableBadgeTarget.innerHTML = `<strong>${bookable.labelValue}</strong>`
    } else {
      this.bookableBadgeTarget.innerHTML = ''
    }
  }

  toggleDatePicker (event) {
    if (this.flatpickr) {
      this.flatpickr.toggle()
    }
  }

  openDatePicker (event) {
    if (this.flatpickr) {
      this.flatpickr.open()
    }
  }

  closeDatePicker (event) {
    if (this.flatpickr) {
      this.flatpickr.close()
    }
  }

  // DATE HELPERS:

  escapeDateFormat (string) {
    return string.replace(/[dDljJwWFmnMUuyYZHhGiSsK]/g, '\\$&')
  }

  get value () {
    return super.value
  }

  set value(newValue) {
    super.value = newValue
    this.refresh()
  }

  get rightNow() {
    let date = new Date()
    return new Date(date.getTime() + (BROWSER_UTC_OFFSET * 1000))
  }

  get humanValue() {
    let date = this.value

    if (this.flatpickr && date) {
      if (this.isToday) {
        return this.todayLabelValue
      } else if (this.isCurrentWeek) {
        return this.flatpickr.formatDate(date, this.currentWeekLabelFormatValue)
      } else if (this.isCurrentYear) {
        return this.flatpickr.formatDate(date, this.currentYearLabelFormatValue)
      } else {
        return this.flatpickr.formatDate(date, this.otherYearLabelFormatValue)
      }
    } else {
      return this.selectLabelValue
    }
  }

  get isToday () {
    let currentDate = this.value
    if (!currentDate) { return false }
    currentDate.setHours(0, 0, 0, 0)

    let rightNow = this.rightNow
    rightNow.setHours(0, 0, 0, 0)

    return rightNow.getTime() == currentDate.getTime()
  }

  get isCurrentWeek () {
    let currentDate = this.value
    if (!currentDate) { return false }

    let rightNow = this.rightNow

    let monday = new Date(rightNow)
    monday.setDate(rightNow.getDate() - rightNow.getDay() + 1)
    monday.setHours(0, 0, 0, 0)

    let sunday = new Date(monday)
    sunday.setDate(monday.getDate() + 6)
    sunday.setHours(23, 59, 59, 999)

    return currentDate >= monday && currentDate <= sunday
  }

  get isCurrentYear () {
    let currentDate = this.value
    if (!currentDate) { return false }

    let rightNow = this.rightNow
    return rightNow.getFullYear() == currentDate.getFullYear()
  }

  // DATE PICKER CALLBACKS:

  isDisabledDate (date) {
    return this.widget.isDisabledDate(date)
  }

  // DATE PICKER EVENTS:

  setDatePickerConfig (setting, value) {
    let current = this.flatpickr.config[setting]

    if (this.isDifferentObject(current, value)) {
      this.flatpickr.set(setting, value)
    }
  }

  // Gets triggered once the calendar is in a ready state.
  datePickerReady (selectedDates, dateAsString, flatpickr) {
    super.datePickerReady(selectedDates, dateAsString, flatpickr)
    this.addBookableBadge()
  }

  // onChange gets triggered when the user selects a date, or changes the time on a selected date.
  datePickerChanged (selectedDates, dateAsString, flatpickr) {
    if (dateAsString) {
      this.lastValue = flatpickr.parseDate(dateAsString)
    }

    let bookable = this.bookable
    if (bookable) {
      bookable.fetchRangedAvailability(this.value, this.value)
    }

    this.addBookableBadge()
  }

  // Gets triggered when the calendar is opened.
  datePickerOpened (selectedDates, dateAsString, flatpickr) {
    super.datePickerOpened(selectedDates, dateAsString, flatpickr)
    this.refreshDatePickerAvailability(flatpickr)

    document.querySelector('.reservations-widget-step-one').classList.add('date-select-open')
    this.addBookableBadge()

    if (this.hasAvailableFromValue || this.hasAvailableTillValue) {
      this.jumpToDateWindow()
    }

    if (this.embedded) {
      this.embedded.title = this.selectLabelValue
      this.embedded.reportWidgetSize()
    }
  }

  // Gets triggered when the calendar is closed.
  datePickerClosed (selectedDates, dateAsString, flatpickr) {
    document.querySelector('.reservations-widget-step-one').classList.remove('date-select-open')

    if (this.embedded) {
      this.embedded.resetTitle()
      this.embedded.reportWidgetSize()
    }
  }

  // Gets triggered when the month is changed, either by the user or programmatically.
  datePickerMonthChanged (selectedDates, dateAsString, flatpickr) {
    super.datePickerMonthChanged(selectedDates, dateAsString, flatpickr)

    this.refreshDatePickerAvailability(flatpickr)

    if (this.embedded) {
      this.embedded.reportWidgetSize()
    }
  }

  // Gets triggered when the year is changed, either by the user or programmatically.
  datePickerYearChanged (selectedDates, dateAsString, flatpickr) {
    super.datePickerYearChanged(selectedDates, dateAsString, flatpickr)

    this.refreshDatePickerAvailability(flatpickr)

    if (this.embedded) {
      this.embedded.reportWidgetSize()
    }
  }

  // Take full control of every date cell by interacting with dayElement.
  createDatePickerDay (selectedDates, dateAsString, flatpickr, dayElement) {
    super.createDatePickerDay(selectedDates, dateAsString, flatpickr, dayElement)

    let bookable = this.bookable
    let date     = dayElement.dateObj
    if (bookable && date) {
      if (bookable.isConfirmableDate(date)) {
        let confirmableElement = document.createElement('small')
        confirmableElement.classList.add('datepicker-confirmable')

        dayElement.appendChild(confirmableElement)
        dayElement.classList.add('is-confirmable-date')
      }

      if (bookable.isDisabledDate(date)) {
        dayElement.classList.add('is-disabled-date')
      }

      if (bookable.isUnavailableDate(date)) {
        let unavailableElement = document.createElement('small')
        unavailableElement.classList.add('datepicker-unavailable')

        dayElement.appendChild(unavailableElement)
        dayElement.classList.add('is-unavailable-date')
      }
    }
  }

  refreshDatePickerAvailability (flatpickr) {
    let firstDate = new Date(flatpickr.currentYear, flatpickr.currentMonth,     1) // first day of current month
    let lastDate  = new Date(flatpickr.currentYear, flatpickr.currentMonth + 2, 0) // last day of next month
    let booking   = this.widget.booking

    if (booking) {
      booking.fetchRangedAvailability(firstDate, lastDate)
    }
  }
}
